import React, { useMemo } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import Dashboard from '../components/Dashboard';
import ActionBriefing from '../components/dashboard/ActionBriefing';
import SummaryStrip from '../components/dashboard/SummaryStrip';
import OperationalRequestEntrySection from '../components/workspace/OperationalRequestEntrySection';
import OperationalPerformanceViews from '../components/workspace/OperationalPerformanceViews';
import OperationalSummaryGrid from '../components/workspace/OperationalSummaryGrid';
import OperationalSharedCalendar from '../components/workspace/OperationalSharedCalendar';
import OperationalRequestWorkspace from '../components/workspace/OperationalRequestWorkspace';
import TodayQueueWidget from '../components/today/TodayQueueWidget';
import ReturnedItemsBanner from '../components/workspace/ReturnedItemsBanner';
import { getOpsWorkspaceMeta } from '../components/workspace/operationalWorkspaceMeta';
import { formatRoleLabel } from '../config/appRoutes';
import useOperationalRequestQueue from '../hooks/useOperationalRequestQueue';
import useCurrentManagerRecord from '../hooks/useCurrentManagerRecord';
import useLocationPerformanceContext from '../hooks/useLocationPerformanceContext';
import useReturnedApprovalItems from '../hooks/useReturnedApprovalItems';
import { useTenantModules } from '../context/TenantConfigContext';
import './RoleDashboards.css';

function formatPercent(value) {
  return `${Math.round(value)}%`;
}

function OpsManagerDashboard({
  authorityLabel = '',
  currentPath,
  dashboardData,
  onNavigate = () => {},
  period,
  selectedManager,
  scopeLabel = '',
  stationSummary,
  myRequests,
  userDisplayName = '',
  userReferenceTitle = '',
  user,
  workspaceLabel = 'Assigned Workspace'
}) {
  const { isModuleEnabled } = useTenantModules();
  const {
    heroDescription,
    heroTitle,
    isDashboardRoute,
    isRequestsRoute,
    isStationRoute
  } = getOpsWorkspaceMeta(currentPath);
  const { performanceContext, updatePerformanceContext } = useLocationPerformanceContext(stationSummary.locationCode);
  const canSubmitRequests = Boolean(user?.permissions?.includes('submit_vacation_request'));
  const tasksEnabled = isModuleEnabled('tasks');
  const requestsEnabled = isModuleEnabled('requests');
  const returnedItems = useReturnedApprovalItems();
  const {
    filteredRequests,
    localRequests,
    requestFilter,
    requestSummary,
    selectedRequest,
    selectedRequestId,
    setLocalRequests,
    setRequestFilter,
    setSelectedRequestId
  } = useOperationalRequestQueue({
    initialFilter: 'all',
    requests: myRequests
  });

  const managerRecord = dashboardData?.managers?.[selectedManager] || null;
  const currentManagerRecord = useCurrentManagerRecord({
    fallbackRoleTitle: formatRoleLabel('ops_manager'),
    sourceRecord: managerRecord,
    user
  });
  const upcomingTimeOff = useMemo(
    () => localRequests.filter(request => new Date(`${request.startDate}T00:00:00`) >= new Date()).length,
    [localRequests]
  );
  const summaryItems = useMemo(() => ([
    {
      label: 'Location At Work',
      value: formatPercent(stationSummary.atWorkRate),
      subtitle: `${stationSummary.managerCount} managers in the current location view`,
      tone: 'good',
      icon: 'fa-user-check'
    },
    ...(requestsEnabled ? [
      {
        label: 'Pending Requests',
        value: requestSummary.pending + requestSummary.reviewNeeded,
        subtitle: 'Requests still waiting on a final outcome',
        tone: 'info',
        icon: 'fa-envelope-open-text'
      },
      {
        label: 'Upcoming Time Off',
        value: upcomingTimeOff,
        subtitle: 'Future requests already on your calendar',
        tone: 'neutral',
        icon: 'fa-plane-departure'
      }
    ] : []),
    {
      label: 'Location KPI Groups',
      value: stationSummary.kpiCount,
      subtitle: 'Tracked KPI groups in the current scorecard',
      tone: 'warning',
      icon: 'fa-chart-column'
    },
    {
      label: 'Location Code',
      value: stationSummary.locationCode || '-',
      subtitle: stationSummary.locationName || 'Assigned location',
      tone: 'neutral',
      icon: 'fa-building'
    }
  ]), [requestSummary, requestsEnabled, stationSummary, upcomingTimeOff]);
  const dashboardSnapshotItems = useMemo(() => ([
    {
      key: 'at-work',
      label: 'Location At Work',
      value: formatPercent(stationSummary.atWorkRate),
      description: `${stationSummary.managerCount} people included`
    },
    {
      key: 'pending-requests',
      label: 'My Pending Requests',
      value: requestSummary.pending + requestSummary.reviewNeeded,
      description: 'Awaiting a decision or flagged for review'
    },
    {
      key: 'scorecard',
      label: 'My Scorecard',
      value: stationSummary.kpiCount,
      description: 'KPI groups — tap to view scorecard ',
      isLink: true,
      onClick: () => onNavigate('/location-scorecard'),
      title: 'Go to your full location scorecard'
    }
  ]), [onNavigate, requestSummary.pending, requestSummary.reviewNeeded, stationSummary.atWorkRate, stationSummary.kpiCount, stationSummary.managerCount]);
  const stationSnapshotItems = useMemo(() => ([
    {
      key: 'at-work',
      label: 'Location At Work',
      value: formatPercent(stationSummary.atWorkRate),
      description: `${stationSummary.managerCount} people included`
    },
    {
      key: 'location-requests',
      label: 'Location Vacation Requests',
      value: stationSummary.pendingRequests,
      description: 'Pending requests across your location'
    },
    {
      key: 'kpis',
      label: 'Open KPI Categories',
      value: stationSummary.kpiCount,
      description: 'Tracked KPI groups in your scorecard'
    }
  ]), [stationSummary.atWorkRate, stationSummary.kpiCount, stationSummary.managerCount, stationSummary.pendingRequests]);
  // Sprint 3B.5: OpsManagerDashboard migrated to WorkspacePage with
  // width="operational". The OperationalWorkspaceHero contents are
  // re-mapped onto WorkspacePage's eyebrow / title / subtitle /
  // contextSlot — identical pattern to AirportManagerDashboard.
  // The .role-page wrapper is retained as an INNER element so every
  // existing RoleDashboards.css selector keeps applying.
  const headerEyebrow = (
    <span className="role-badge">{workspaceLabel}</span>
  );
  const headerContextChips = (
    <div className="role-context-row">
      <div className="role-scope-chip" title="The signed-in person using this workspace.">
        <span>Profile</span>
        <strong>{userDisplayName || user?.email || 'Authenticated user'}</strong>
      </div>
      {userReferenceTitle ? (
        <div className="role-scope-chip" title="Job title stored for reference.">
          <span>Title</span>
          <strong>{userReferenceTitle}</strong>
        </div>
      ) : null}
      <div className="role-scope-chip" title="The active scope that filters visible records, scorecards, and requests.">
        <span>Scope</span>
        <strong>{scopeLabel || stationSummary.locationCode || 'Assigned location'}</strong>
      </div>
      {authorityLabel ? (
        <div className="role-scope-chip" title="Additional permissions granted to this account beyond its default workspace permissions.">
          <span>Permissions</span>
          <strong>{authorityLabel}</strong>
        </div>
      ) : null}
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="ops-manager-dashboard"
      width="operational"
      eyebrow={headerEyebrow}
      title={heroTitle}
      subtitle={heroDescription}
      contextSlot={headerContextChips}
    >
      <div className="role-page">
        <SummaryStrip items={summaryItems} />

      <ReturnedItemsBanner items={returnedItems} />

      {tasksEnabled ? (
        <TodayQueueWidget
          title="Today"
          subtitle="Action items and actuals that need your attention."
          typeFilter="action-item,actuals"
          onNavigate={onNavigate}
          hideWhenEmpty
        />
      ) : null}

      <OperationalPerformanceViews
        currentPath={currentPath}
        dashboardData={dashboardData}
        locationCode={stationSummary.locationCode}
        locationName={stationSummary.locationName}
        onPerformanceContextChange={updatePerformanceContext}
        period={period}
        performanceContext={performanceContext}
        selectedManager={selectedManager}
      />

      {isDashboardRoute && requestsEnabled && (
        <ActionBriefing
          heading="Open action items"
          items={[
            { count: returnedItems.length, label: `action item${returnedItems.length !== 1 ? 's' : ''} returned for revision`, tone: 'danger', path: '/requests-calendar', onNavigate },
            { count: requestSummary.pending + requestSummary.reviewNeeded, label: 'request action items pending a final decision', tone: 'info', path: '/requests-calendar', onNavigate }
          ]}
        />
      )}

      {isDashboardRoute && (
        <div className="role-dashboard-shell role-dashboard-shell-ops">
          <div className="role-layout-main">
            <Dashboard
              data={dashboardData}
              period={period}
              manager={selectedManager}
              dataVersion={0}
            />
          </div>

          <div className="role-layout-side role-stack">
            <OperationalSummaryGrid
              className="role-summary-grid-compact"
              items={dashboardSnapshotItems.map((item) => item.key === 'scorecard'
                ? {
                    ...item,
                    description: (
                      <>
                        KPI groups — tap to view scorecard <i className="fas fa-arrow-right" aria-hidden="true" />
                      </>
                    )
                  }
                : item)}
            />

            <div className="role-section">
              <div className="role-section-header">
                <h2>Location Summary</h2>
                <span>{stationSummary.locationCode}</span>
              </div>
              <p className="role-section-copy">
                Location summary for context only — individual peer performance is not shown. To
                review your own KPIs in full, use the{' '}
                <button
                  type="button"
                  className="role-inline-link"
                  onClick={() => onNavigate('/location-scorecard')}
                >
                  Location Scorecard
                </button>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {isStationRoute && (
        <>
          <OperationalSummaryGrid items={stationSnapshotItems} />

          <div className="role-section">
            <div className="role-section-header">
              <h2>Location Summary</h2>
              <span>{stationSummary.locationCode}</span>
            </div>
            <p className="role-section-copy">
              This shared location view provides local operating context without revealing
              unrestricted peer performance details.
            </p>
          </div>
        </>
      )}

      {isRequestsRoute && requestsEnabled && (
        <div className="role-stack">
          <OperationalRequestEntrySection
            canSubmit={canSubmitRequests}
            currentManager={currentManagerRecord}
            disabledMessage="This account can view request history, but `Submit Vacation Requests` is turned off for the current access profile. Ask an authorized reviewer to enable that permission if this person should be able to submit time off."
            onSubmitted={(event) => {
              setLocalRequests(current => [event, ...current.filter(item => String(item.id) !== String(event.id))]);
              setSelectedRequestId(String(event.id));
            }}
            scopeCode={stationSummary.locationCode}
            submitCopy="Submit your own PTO request here. Blackout and coverage rules are checked before the request enters the approval queue."
            title="Request Time Off"
          />
          <div className="role-section">
            <OperationalRequestWorkspace
              sectionTitle="My Vacation Requests"
              countLabel="shown"
              introCopy="Every request shown here is live from the request workflow. Denied requests keep their reason, and approved or denied items can be opened to review the audit trail."
              requestFilter={requestFilter}
              requestSummary={requestSummary}
              includeAll
              queueAllLabel="All My Requests"
              filteredRequests={filteredRequests}
              isInitialLoading={filteredRequests.length === 0 && localRequests === myRequests && myRequests.length === 0 && !requestSummary.approved && !requestSummary.denied}
              emptyMessage="No vacation requests found."
              noResultsColSpan={5}
              selectedRequestId={selectedRequestId}
              skeletonColumnCount={5}
              tableClassName="ops-requests-table"
              detailRequest={selectedRequest}
              detailRequestTitle="Selected Request"
              detailEmptyMessage="Select a request above to view the latest decision details and history."
              detailHistoryTitle="Approval / Denial History"
              onSelectQueue={setRequestFilter}
              onSelectRequest={setSelectedRequestId}
            />
          </div>
        </div>
      )}

      {isRequestsRoute && (
        <OperationalSharedCalendar
          locationCode={stationSummary.locationCode}
          locationLabel={stationSummary.locationName}
          subtitle="Shared station calendar for approved time off, blackout dates, and visible coverage-rule markers."
          title="Shared Time-Off Calendar"
          workspaceLabel={workspaceLabel}
        />
      )}
      </div>
    </WorkspacePage>
  );
}

export default OpsManagerDashboard;
